import type {
  InvoicePossibleAssigneesPagedParams,
  InvoicePossibleAssigneesResponse,
} from './types';

import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoicePossibleAssignees } from './fetchInvoicePossibleAssignees';

/**
 * Generates query options for users who can be assigned to an invoice.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Additional query options.
 * @returns Query options for possible invoice assignees.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/list_possible_assignees_invoices__invoice_id__possible_assignees_get
 */
export function getInvoicePossibleAssigneesQueryOptions<
  TData = InvoicePossibleAssigneesResponse,
>({
  invoiceId,
  options,
}: {
  invoiceId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<InvoicePossibleAssigneesResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) {
  return queryOptions<InvoicePossibleAssigneesResponse, Error, TData>({
    // Optional data: a failed load must neither throw into the route error
    // boundary nor raise the global error toast.
    meta: { suppressError: true },
    queryFn: invoiceId
      ? (context: QueryFunctionContext) =>
          fetchInvoicePossibleAssignees({ invoiceId }, context)
      : skipToken,
    queryKey: queryKeysInvoice.getPossibleAssignees(invoiceId ?? ''),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });
}

/**
 * Loads users who can be assigned to an invoice.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Additional query options.
 * @returns TanStack Query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/list_possible_assignees_invoices__invoice_id__possible_assignees_get
 */
export function useQueryInvoicePossibleAssignees<
  TData = InvoicePossibleAssigneesResponse,
>(
  invoiceId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<InvoicePossibleAssigneesResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery(
    getInvoicePossibleAssigneesQueryOptions<TData>({ invoiceId, options }),
  );
}

/**
 * Generates infinite query options for the users who can be assigned to an
 * invoice, one page per offset. The selected data is the flattened list of
 * every loaded user plus whether another page exists.
 *
 * @param queryParams - Invoice identifier and optional search term.
 * @returns Infinite query options for possible invoice assignees.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/list_possible_assignees_invoices__invoice_id__possible_assignees_get
 */
export function getInvoicePossibleAssigneesInfiniteQueryOptions({
  invoiceId,
  searchString,
}: InvoicePossibleAssigneesPagedParams) {
  return infiniteQueryOptions({
    getNextPageParam: (lastPage, pages) =>
      lastPage.hasNextPage
        ? pages.length * PAGINATION_PAGE_SIZE_DEFAULT
        : undefined,
    initialPageParam: 0,
    meta: { suppressError: true },
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam, signal }) =>
      fetchInvoicePossibleAssignees(
        {
          invoiceId,
          limit: PAGINATION_PAGE_SIZE_DEFAULT,
          offset: pageParam,
          searchString,
        },
        { signal },
      ),
    queryKey: queryKeysInvoice.getPossibleAssigneesPaged({
      invoiceId,
      searchString,
    }),
    select: (data) => ({
      hasNextPage: data.pages.at(-1)?.hasNextPage ?? false,
      people: data.pages.flatMap((page) => page.data),
    }),
    staleTime: ms('60s'),
    throwOnError: false,
  });
}

/**
 * Loads the users who can be assigned to an invoice, one page at a time.
 *
 * @param queryParams - Invoice identifier and optional search term.
 * @param options - Whether the query runs at all.
 * @returns TanStack infinite query result; `data` holds the loaded users and `hasNextPage`.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/list_possible_assignees_invoices__invoice_id__possible_assignees_get
 */
export function useInfiniteQueryInvoicePossibleAssignees(
  queryParams: InvoicePossibleAssigneesPagedParams,
  options?: { readonly enabled?: boolean },
) {
  return useInfiniteQuery({
    ...getInvoicePossibleAssigneesInfiniteQueryOptions(queryParams),
    ...options,
  });
}
